import { useEffect, useMemo, useRef, useState } from 'react';
import { fetchViewingHistory } from '../services/library.service';
import { useUserState } from '../providers/user-state.provider.ts';
import {
    buildContinueWatchingItems,
    ContinueWatchingItem
} from '../utils/continue-watching';

type ContinueWatchingStatus = 'idle' | 'loading' | 'success' | 'error';

interface ContinueWatchingState {
    ownerToken: string | null;
    items: ContinueWatchingItem[];
    status: ContinueWatchingStatus;
}

const EMPTY_CONTINUE_WATCHING_ITEMS: ContinueWatchingItem[] = [];

export interface UseContinueWatchingOptions {
    /** Maximum cards to expose. */
    limit?: number;
    /** Current video URL to hide from the rail. */
    excludeVideoUrl?: string | null;
    /** Current video ID to hide from the rail. */
    excludeVideoId?: string | null;
}

export interface UseContinueWatchingResult {
    items: ContinueWatchingItem[];
    status: ContinueWatchingStatus;
    isLoading: boolean;
}

/**
 * Fetches signed-in viewing history and exposes unfinished videos as resume cards.
 */
export const useContinueWatching = ({
    limit = 6,
    excludeVideoUrl,
    excludeVideoId
}: UseContinueWatchingOptions = {}): UseContinueWatchingResult => {
    const { getUserInfo, isLoggedIn, isUserStateLoading } = useUserState();
    const token = getUserInfo()?.token ?? null;
    const tokenRef = useRef(token);
    tokenRef.current = token;
    const [state, setState] = useState<ContinueWatchingState>({
        ownerToken: null,
        items: [],
        status: 'idle'
    });

    useEffect(() => {
        if (isUserStateLoading) {
            return;
        }

        if (!isLoggedIn || !token) {
            setState({ ownerToken: null, items: [], status: 'idle' });
            return;
        }

        let isMounted = true;
        const requestedToken = token;
        setState((current) => ({
            ownerToken: requestedToken,
            items: current.ownerToken === requestedToken ? current.items : [],
            status: 'loading'
        }));

        const pageSize = Math.max(20, limit * 3);
        fetchViewingHistory(requestedToken, 1, pageSize)
            .then((response) => {
                if (!isMounted || tokenRef.current !== requestedToken) {
                    return;
                }

                setState({
                    ownerToken: requestedToken,
                    items: buildContinueWatchingItems(response.Results ?? [], {
                        limit,
                        excludeVideoUrls: excludeVideoUrl ? [excludeVideoUrl] : [],
                        excludeVideoIds: excludeVideoId ? [excludeVideoId] : []
                    }),
                    status: 'success'
                });
            })
            .catch(() => {
                if (!isMounted || tokenRef.current !== requestedToken) {
                    return;
                }

                setState({ ownerToken: requestedToken, items: [], status: 'error' });
            });

        return () => {
            isMounted = false;
        };
    }, [excludeVideoId, excludeVideoUrl, isLoggedIn, isUserStateLoading, limit, token]);

    const ownsVisibleState = state.ownerToken === token;
    const visibleItems = ownsVisibleState ? state.items : EMPTY_CONTINUE_WATCHING_ITEMS;
    const visibleStatus: ContinueWatchingStatus = isUserStateLoading
        ? 'loading'
        : !isLoggedIn || !token
            ? 'idle'
            : ownsVisibleState
                ? state.status
                : 'loading';

    return useMemo(() => ({
        items: visibleItems,
        status: visibleStatus,
        isLoading: visibleStatus === 'loading'
    }), [visibleItems, visibleStatus]);
};
